import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Castle,
  Clock,
  Filter,
  Phone,
  CalendarDays,
  CalendarRange,
  LayoutGrid
} from "lucide-react";
import { toast } from "sonner";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameDay, 
  addMonths, 
  subMonths,
  addWeeks,
  subWeeks,
  isToday,
  parseISO,
  eachMonthOfInterval
} from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time slots configuration for the week view
const HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8h to 19h

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStructure, setSelectedStructure] = useState("all");
  const [structures, setStructures] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // month, week, week-full

  useEffect(() => {
    fetchStructures();
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [currentDate, selectedStructure, view]);

  const fetchStructures = async () => {
    try {
      const response = await fetch(`${API}/catalogue/structures`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStructures([{ id: "all", name: "Toutes les structures" }, ...data]);
      }
    } catch (error) {
    }
  };

  const fetchReservations = async () => {
    try {
      let start, end;
      if (view === "month") {
        // Fetch 4 months of data for the multi-month view
        start = format(startOfMonth(currentDate), "yyyy-MM-dd");
        end = format(endOfMonth(addMonths(currentDate, 3)), "yyyy-MM-dd");
      } else {
        start = format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
        end = format(endOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      }
      
      let url = `${API}/reservations?date_from=${start}&date_to=${end}`;
      if (selectedStructure !== "all") {
        url += `&structure_id=${selectedStructure}`;
      }

      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch");
      
      setReservations(await response.json());
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const reservationsByDate = useMemo(() => {
    const map = {};
    reservations.forEach((r) => {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    });
    return map;
  }, [reservations]);

  const selectedDateReservations = useMemo(() => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return reservationsByDate[dateStr] || [];
  }, [selectedDate, reservationsByDate]);

  // Week view days
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  // Multi-month view: 4 months starting from current
  const monthsToShow = useMemo(() => {
    return eachMonthOfInterval({
      start: startOfMonth(currentDate),
      end: endOfMonth(addMonths(currentDate, 3))
    });
  }, [currentDate]);

  const handlePrev = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 4));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 4));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "DEMANDE_EN_LIGNE": return "bg-cyan-400 border-cyan-500";
      case "OPTION": return "bg-amber-400 border-amber-500";
      case "CONFIRME": return "bg-emerald-500 border-emerald-600";
      case "ANNULE": return "bg-red-400 border-red-500";
      case "BLOQUE": return "bg-gray-400 border-gray-500";
      default: return "bg-gray-400 border-gray-500";
    }
  };

  const getStatusBgLight = (status) => {
    switch (status) {
      case "DEMANDE_EN_LIGNE": return "bg-cyan-100 hover:bg-cyan-200 border-cyan-300";
      case "OPTION": return "bg-amber-100 hover:bg-amber-200 border-amber-300";
      case "CONFIRME": return "bg-emerald-100 hover:bg-emerald-200 border-emerald-300";
      case "ANNULE": return "bg-red-100 hover:bg-red-200 border-red-300";
      case "BLOQUE": return "bg-gray-100 hover:bg-gray-200 border-gray-300";
      default: return "bg-gray-100 hover:bg-gray-200 border-gray-300";
    }
  };

  // Check if reservation is unviewed (for notification badge)
  const isUnviewed = (reservation) => {
    return reservation.status === "DEMANDE_EN_LIGNE" && !reservation.viewed_at;
  };

  const structureNames = useMemo(() => {
    const map = {};
    structures.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [structures]);

  const timeSlotNames = {
    "matin": "Matin",
    "apres-midi": "Après-midi",
    "journee": "Journée",
  };

  // Get time slot position for week-full view
  const getTimeSlotStyle = (timeSlot) => {
    switch (timeSlot) {
      case "matin":
        return { top: "0%", height: "41.67%" }; // 8h-13h (5h sur 12h)
      case "apres-midi":
        return { top: "50%", height: "41.67%" }; // 14h-19h (5h sur 12h)
      case "journee":
        return { top: "0%", height: "91.67%" }; // 8h-19h (11h sur 12h)
      default:
        return { top: "0%", height: "50%" };
    }
  };

  // Check if two time slots overlap
  const slotsOverlap = (slot1, slot2) => {
    // journee overlaps with everything
    if (slot1 === "journee" || slot2 === "journee") return true;
    // matin and apres-midi don't overlap with each other
    return slot1 === slot2;
  };

  // Group reservations that overlap and calculate their positions (Google Calendar style)
  const getReservationsLayout = (dayReservations) => {
    if (dayReservations.length === 0) return {};

    // Find all groups of overlapping reservations
    const groups = [];
    const assigned = new Set();

    dayReservations.forEach((r1, i) => {
      if (assigned.has(r1.id)) return;

      // Start a new group with this reservation
      const group = [r1];
      assigned.add(r1.id);

      // Find all other reservations that overlap with this group
      let foundNew = true;
      while (foundNew) {
        foundNew = false;
        dayReservations.forEach((r2) => {
          if (assigned.has(r2.id)) return;
          
          // Check if r2 overlaps with any reservation in the group
          const overlaps = group.some(gr => slotsOverlap(gr.time_slot, r2.time_slot));
          if (overlaps) {
            group.push(r2);
            assigned.add(r2.id);
            foundNew = true;
          }
        });
      }

      groups.push(group);
    });

    // Calculate layout for each group
    const layoutMap = {};
    groups.forEach(group => {
      const totalInGroup = group.length;
      group.forEach((r, idx) => {
        layoutMap[r.id] = {
          index: idx,
          total: totalInGroup,
          slot: r.time_slot
        };
      });
    });

    return layoutMap;
  };

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Planning" />
      
      <main className="p-4 space-y-4 max-w-7xl mx-auto">
        {/* View Toggle & Filters */}
        <Card className="card-festive">
          <CardContent className="p-4 space-y-3">
            {/* View Toggle */}
            <Tabs value={view} onValueChange={setView} className="w-full">
              <TabsList className="w-full grid grid-cols-3 h-10 rounded-xl bg-pink-100/50">
                <TabsTrigger value="month" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm" data-testid="view-month-btn">
                  <CalendarDays className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Mois</span>
                </TabsTrigger>
                <TabsTrigger value="week" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm" data-testid="view-week-btn">
                  <CalendarRange className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Semaine</span>
                </TabsTrigger>
                <TabsTrigger value="week-full" className="rounded-lg data-[state=active]:bg-white text-xs md:text-sm" data-testid="view-week-full-btn">
                  <LayoutGrid className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Planning</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Structure Filter */}
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-festive-pink flex-shrink-0" />
              <Select value={selectedStructure} onValueChange={setSelectedStructure}>
                <SelectTrigger data-testid="structure-filter" className="flex-1 h-10 rounded-xl border-2 border-pink-200 focus:border-festive-pink">
                  <SelectValue placeholder="Filtrer par structure" />
                </SelectTrigger>
                <SelectContent>
                  {structures.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Navigation Header */}
        <Card className="card-festive overflow-hidden">
          <CardHeader className="py-3 bg-gradient-to-r from-festive-pink/10 to-festive-purple/10">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={handlePrev}
                className="rounded-xl"
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="text-center">
                <CardTitle className="font-heading capitalize text-base md:text-xl">
                  {view === "month" 
                    ? `${format(monthsToShow[0], "MMMM", { locale: fr })} - ${format(monthsToShow[3], "MMMM yyyy", { locale: fr })}`
                    : `Semaine du ${format(weekDays[0], "d", { locale: fr })} au ${format(weekDays[6], "d MMMM yyyy", { locale: fr })}`
                  }
                </CardTitle>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleToday}
                  className="text-festive-pink hover:text-festive-purple h-6"
                >
                  Aujourd&apos;hui
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNext}
                className="rounded-xl"
                data-testid="next-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>

          {/* Calendar Content */}
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-festive-pink border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : view === "week-full" ? (
              /* Full Week View - Google Calendar Style */
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Days Header */}
                  <div className="grid grid-cols-8 border-b border-gray-200">
                    <div className="p-2 text-center text-xs text-gray-500 border-r border-gray-200">
                      GMT+4
                    </div>
                    {weekDays.map((day) => {
                      const today = isToday(day);
                      return (
                        <div
                          key={day.toISOString()}
                          className={`p-2 text-center border-r border-gray-200 last:border-r-0 ${
                            today ? "bg-festive-pink/10" : ""
                          }`}
                        >
                          <div className={`text-xs uppercase tracking-wide ${today ? "text-festive-pink font-semibold" : "text-gray-500"}`}>
                            {format(day, "EEE", { locale: fr })}
                          </div>
                          <div className={`text-xl font-bold mt-1 ${
                            today 
                              ? "w-10 h-10 mx-auto rounded-full bg-festive-pink text-white flex items-center justify-center" 
                              : "text-gray-900"
                          }`}>
                            {format(day, "d")}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Time Grid */}
                  <div className="relative" style={{ height: "600px" }}>
                    {/* Hour lines */}
                    <div className="absolute inset-0 grid grid-cols-8">
                      {/* Time labels column */}
                      <div className="border-r border-gray-200">
                        {HOURS.map((hour) => (
                          <div
                            key={hour}
                            className="h-[50px] border-b border-gray-100 pr-2 text-right"
                          >
                            <span className="text-xs text-gray-400 relative -top-2">
                              {hour}:00
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Day columns */}
                      {weekDays.map((day) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const dayReservations = reservationsByDate[dateStr] || [];
                        const today = isToday(day);
                        const layout = getReservationsLayout(dayReservations);

                        return (
                          <div
                            key={day.toISOString()}
                            className={`relative border-r border-gray-200 last:border-r-0 ${
                              today ? "bg-festive-pink/5" : ""
                            }`}
                          >
                            {/* Hour grid lines */}
                            {HOURS.map((hour) => (
                              <div
                                key={hour}
                                className="h-[50px] border-b border-gray-100"
                              />
                            ))}

                            {/* Reservations */}
                            {dayReservations.map((reservation) => {
                              const style = getTimeSlotStyle(reservation.time_slot);
                              const statusBg = getStatusBgLight(reservation.status);
                              const layoutInfo = layout[reservation.id];
                              const widthPercent = 100 / layoutInfo.total;
                              const leftPercent = layoutInfo.index * widthPercent;
                              const showNotif = isUnviewed(reservation);
                              
                              return (
                                <div
                                  key={reservation.id}
                                  onClick={() => navigate(`/reservations/${reservation.id}`)}
                                  className={`absolute rounded-lg border-l-4 p-1 cursor-pointer transition-colors overflow-hidden ${statusBg} ${getStatusColor(reservation.status).split(' ')[1]}`}
                                  style={{
                                    top: style.top,
                                    height: style.height,
                                    left: `calc(${leftPercent}% + 2px)`,
                                    width: `calc(${widthPercent}% - 4px)`,
                                    zIndex: 10,
                                  }}
                                  data-testid={`week-reservation-${reservation.id}`}
                                >
                                  {/* Notification badge for unviewed */}
                                  {showNotif && (
                                    <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                  )}
                                  <div className="text-xs font-semibold truncate">
                                    {reservation.client_name}
                                  </div>
                                  <div className="text-xs text-gray-600 truncate">
                                    {structureNames[reservation.structure_id]}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {timeSlotNames[reservation.time_slot]}
                                  </div>
                                  {reservation.client_phone && (
                                    <a
                                      href={`tel:${reservation.client_phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs text-festive-pink hover:underline flex items-center gap-1 mt-1"
                                    >
                                      <Phone className="w-3 h-3" />
                                      {reservation.client_phone}
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : view === "month" ? (
              /* Multi-Month View - 4 months grid */
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {monthsToShow.map((month) => (
                    <MiniMonthCalendar
                      key={month.toISOString()}
                      month={month}
                      selectedDate={selectedDate}
                      onSelectDate={setSelectedDate}
                      reservationsByDate={reservationsByDate}
                      getStatusColor={getStatusColor}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Compact Week View */
              <div className="p-4">
                <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium text-gray-500 mb-2">
                  {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
                    <div key={day} className="py-2">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {weekDays.map((day) => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const dayReservations = reservationsByDate[dateStr] || [];
                    const isSelected = isSameDay(day, selectedDate);
                    const today = isToday(day);
                    
                    return (
                      <button
                        key={dateStr}
                        onClick={() => setSelectedDate(day)}
                        className={`min-h-[100px] p-2 rounded-xl border-2 transition-colors ${
                          isSelected
                            ? "border-festive-pink bg-pink-50"
                            : today
                            ? "border-festive-purple/50 bg-purple-50/50"
                            : "border-gray-100 hover:border-pink-200"
                        }`}
                      >
                        <div className={`text-sm font-semibold mb-1 ${today ? "text-festive-purple" : ""}`}>
                          {format(day, "d")}
                        </div>
                        <div className="space-y-1">
                          {dayReservations.slice(0, 3).map((r) => (
                            <div
                              key={r.id}
                              className={`text-xs px-1 py-0.5 rounded truncate text-white ${getStatusColor(r.status).split(' ')[0]}`}
                            >
                              {r.client_name.split(" ")[0]}
                            </div>
                          ))}
                          {dayReservations.length > 3 && (
                            <div className="text-xs text-gray-500">
                              +{dayReservations.length - 3}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Date Reservations (for month and compact week views) */}
        {view !== "week-full" && (
          <Card className="card-festive">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg font-heading">
                <div className="w-8 h-8 rounded-lg bg-festive-pink/10 flex items-center justify-center">
                  <CalendarIcon className="w-4 h-4 text-festive-pink" />
                </div>
                {format(selectedDate, "EEEE d MMMM", { locale: fr })}
                {isToday(selectedDate) && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-festive-purple text-white">
                    Aujourd&apos;hui
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateReservations.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pink-50 flex items-center justify-center">
                    <Castle className="w-8 h-8 text-pink-300" />
                  </div>
                  <p className="text-gray-500">Aucune réservation ce jour</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDateReservations.map((reservation) => (
                    <div
                      key={reservation.id}
                      data-testid={`calendar-reservation-${reservation.id}`}
                      onClick={() => navigate(`/reservations/${reservation.id}`)}
                      className="p-4 rounded-2xl bg-white border border-pink-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-festive-pink/20 to-festive-purple/20 flex items-center justify-center">
                            <Castle className="w-5 h-5 text-festive-pink" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {reservation.client_name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {structureNames[reservation.structure_id] || reservation.structure_id}
                            </p>
                          </div>
                        </div>
                        <StatusBadge status={reservation.status} showNotification={isUnviewed(reservation)} />
                      </div>

                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-festive-pink" />
                          <span>{timeSlotNames[reservation.time_slot]}</span>
                        </div>
                        <a
                          href={`tel:${reservation.client_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-festive-pink hover:underline"
                        >
                          <Phone className="w-4 h-4" />
                          <span>{reservation.client_phone}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <Card className="card-festive">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Légende</p>
            <div className="flex flex-wrap gap-4">
              <LegendItem color="bg-cyan-400" label="Demande en ligne" />
              <LegendItem color="bg-amber-400" label="Option" />
              <LegendItem color="bg-emerald-500" label="Confirmé" />
              <LegendItem color="bg-red-400" label="Annulé" />
              <LegendItem color="bg-gray-400" label="Bloqué" />
            </div>
          </CardContent>
        </Card>
      </main>

      <BottomNav />
    </div>
  );
}

const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-3 h-3 rounded-full ${color}`} />
    <span className="text-sm text-gray-600">{label}</span>
  </div>
);

// Mini Month Calendar component for the multi-month view
const MiniMonthCalendar = ({ month, selectedDate, onSelectDate, reservationsByDate, getStatusColor }) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="bg-white rounded-xl border border-pink-100 p-3 shadow-sm">
      {/* Month Header */}
      <h3 className="text-center font-semibold text-gray-800 mb-2 capitalize">
        {format(month, "MMMM yyyy", { locale: fr })}
      </h3>
      
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["L", "M", "M", "J", "V", "S", "D"].map((day, idx) => (
          <div key={idx} className="text-center text-xs font-medium text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="space-y-1">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-1">
            {week.map((day) => {
              const dateStr = format(day, "yyyy-MM-dd");
              const dayReservations = reservationsByDate[dateStr] || [];
              const isCurrentMonth = day.getMonth() === month.getMonth();
              const isSelected = isSameDay(day, selectedDate);
              const today = isToday(day);
              
              return (
                <button
                  key={dateStr}
                  onClick={() => onSelectDate(day)}
                  disabled={!isCurrentMonth}
                  className={`
                    relative aspect-square flex flex-col items-center justify-center rounded-md text-xs
                    transition-colors
                    ${!isCurrentMonth ? "text-gray-300 cursor-default" : "hover:bg-pink-50 cursor-pointer"}
                    ${isSelected ? "bg-festive-pink text-white hover:bg-festive-pink" : ""}
                    ${today && !isSelected ? "ring-2 ring-festive-purple ring-inset" : ""}
                  `}
                >
                  <span className={`${isSelected ? "font-bold" : ""}`}>{day.getDate()}</span>
                  {dayReservations.length > 0 && isCurrentMonth && (
                    <div className="flex gap-0.5 mt-0.5">
                      {dayReservations.slice(0, 3).map((r) => (
                        <div
                          key={r.id}
                          className={`w-1 h-1 rounded-full ${isSelected ? "bg-white" : getStatusColor(r.status).split(" ")[0]}`}
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

